import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, X } from "lucide-react";
import { useTeamWorkdays, isOffDay, OffDayIcon } from "../hooks/useTeamWorkdays";

export const BRAND = "#249EEB";

const WEEKDAYS = [
  "SUNDAY",
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
];

const TEAM_PALETTE = [
  { bg: "#EDE9FE", head: "#7C3AED", text: "#5B21B6", border: "#DDD6FE" },
  { bg: "#FDECD8", head: "#EA9A3E", text: "#B45309", border: "#FADCB4" },
  { bg: "#E1F2FD", head: BRAND, text: "#0B5C8F", border: "#BEE3F8" },
  { bg: "#DCFCE7", head: "#22C55E", text: "#15803D", border: "#BBF7D0" },
  { bg: "#FCE7F3", head: "#EC4899", text: "#BE185D", border: "#FBCFE8" },
  { bg: "#FEF9C3", head: "#CA8A04", text: "#A16207", border: "#FEF08A" },
];
const NO_TEAM = { bg: "#F1F5F9", head: "#94A3B8", text: "#475569", border: "#E2E8F0" };

function keyOf(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate(),
  ).padStart(2, "0")}`;
}
function localKey(iso) {
  return keyOf(new Date(iso));
}

function hexToRgba(hex, alpha) {
  if (!hex || typeof hex !== "string") return null;
  let h = hex.replace("#", "").trim();
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length !== 6) return null;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function styleFromColor(color) {
  const head = color || BRAND;
  return {
    head,
    bg: hexToRgba(head, 0.12) || "#EEF2F7",
    border: hexToRgba(head, 0.35) || "#E2E8F0",
    text: head,
  };
}

export function MonthlyTeamGrid({
  cursor,
  jobs,
  clients,
  teams,
  onOpenQuick,
  onToggleDone,
}) {
  // Blocos por equipe começam FECHADOS; clique no cabeçalho expande.
  const [expanded, setExpanded] = useState(() => new Set());
  const workdays = useTeamWorkdays();
  const todayRef = useRef(null);
  const scrolledRef = useRef(false);
  const todayKey = keyOf(new Date());

  // Ao abrir, rola direto até o dia de hoje (só uma vez, se estiver no mês atual).
  useEffect(() => {
    if (scrolledRef.current) return;
    if (todayRef.current) {
      const id = setTimeout(() => {
        todayRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 350);
      scrolledRef.current = true;
      return () => clearTimeout(id);
    }
  });

  const gridStart = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const s = new Date(first);
    s.setDate(first.getDate() - first.getDay());
    s.setHours(0, 0, 0, 0);
    return s;
  }, [cursor]);

  const cells = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      arr.push(d);
    }
    return arr;
  }, [gridStart]);

  const clientName = useCallback(
    (id) => clients.find((c) => c.id === id)?.name || "—",
    [clients],
  );
  const teamName = useCallback(
    (id) => teams.find((t) => t.id === id)?.name || "NO TEAM",
    [teams],
  );
  const teamStyle = useMemo(() => {
    const sorted = [...teams].sort((a, b) =>
      (a.name || "").localeCompare(b.name || ""),
    );
    const map = {};
    sorted.forEach((t, i) => {
      // Usa a cor própria da equipe (cadastro). Sem cor → paleta automática.
      map[t.id] = t.color ? styleFromColor(t.color) : TEAM_PALETTE[i % TEAM_PALETTE.length];
    });
    return (id) => map[id] || NO_TEAM;
  }, [teams]);

  const byDay = useMemo(() => {
    const out = {};
    for (const j of jobs) {
      const day = localKey(j.scheduled_at);
      const tk = j.team_id || "__none__";
      out[day] = out[day] || {};
      out[day][tk] = out[day][tk] || [];
      out[day][tk].push(j);
    }
    // Ordena cada equipe por horário (mais cedo primeiro).
    for (const day of Object.keys(out)) {
      for (const tk of Object.keys(out[day])) {
        out[day][tk].sort((a, b) =>
          (a.scheduled_at || "").localeCompare(b.scheduled_at || ""),
        );
      }
    }
    return out;
  }, [jobs]);

  function toggleExpand(k) {
    setExpanded((prev) => {
      const n = new Set(prev);
      n.has(k) ? n.delete(k) : n.add(k);
      return n;
    });
  }

  const monthIdx = cursor.getMonth();

  return (
    <div className="rounded-xl overflow-hidden border border-slate-200 bg-white">
      <div className="lg:hidden px-3 py-1.5 text-[11px] font-medium text-slate-500 bg-slate-50 border-b border-slate-200 text-center">
        ← deslize para o lado para ver todos os dias →
      </div>
      <div className="overflow-x-auto">
        <div className="min-w-[900px] lg:min-w-0">
      <div className="grid grid-cols-7 bg-blue-600 text-white text-[11px] font-bold uppercase tracking-wider">
        {WEEKDAYS.map((w) => (
          <div
            key={w}
            className="px-2 py-2 text-center border-r border-blue-500 last:border-0"
          >
            {w}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((d) => {
          const dayKey = keyOf(d);
          const inMonth = d.getMonth() === monthIdx;
          const dayGroups = byDay[dayKey] || {};
          const jobCount = Object.values(dayGroups).reduce(
            (s, arr) => s + arr.length,
            0,
          );
          const teamKeys = Object.keys(dayGroups).sort((a, b) => {
            if (a === "__none__") return 1;
            if (b === "__none__") return -1;
            return teamName(a).localeCompare(teamName(b));
          });
          const isToday = dayKey === todayKey;
          return (
            <div
              key={dayKey}
              ref={isToday ? todayRef : undefined}
              data-testid={`monthly-day-${dayKey}`}
              className={`min-h-[130px] border-b border-r p-1.5 ${
                isToday
                  ? "bg-blue-50 border-blue-300 ring-2 ring-blue-500 ring-inset"
                  : `border-slate-200 ${inMonth ? "bg-white" : "bg-slate-50/70"}`
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] font-semibold text-slate-500">
                  {jobCount > 0 ? `${jobCount} ${jobCount === 1 ? "Job" : "Jobs"}` : ""}
                </span>
                {isToday ? (
                  <span
                    className="inline-flex items-center justify-center h-6 min-w-6 px-1.5 rounded-full bg-blue-600 text-white text-xs font-bold shadow-sm"
                    title="Hoje"
                    data-testid={`monthly-today-${dayKey}`}
                  >
                    {d.getDate()}
                  </span>
                ) : (
                  <span
                    className={`text-sm font-bold ${
                      inMonth ? "text-slate-800" : "text-slate-400"
                    }`}
                  >
                    {d.getDate()}
                  </span>
                )}
              </div>
              <div className="space-y-1.5">
                {teamKeys.map((tk) => {
                  const arr = dayGroups[tk];
                  const style = tk === "__none__" ? NO_TEAM : teamStyle(tk);
                  const name = tk === "__none__" ? "NO TEAM" : teamName(tk);
                  const ckey = `${dayKey}:${tk}`;
                  const isExpanded = expanded.has(ckey);
                  const offDay = isOffDay(workdays, tk, d) && arr.some((j) => j.status !== "cancelada");
                  return (
                    <div
                      key={ckey}
                      className="rounded-md overflow-hidden border"
                      style={{ borderColor: style.border, background: style.bg }}
                      data-testid={`monthly-teamblock-${dayKey}-${tk}`}
                    >
                      <button
                        onClick={() => toggleExpand(ckey)}
                        className="w-full flex items-center justify-between px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-white"
                        style={{ background: style.head }}
                      >
                        <span className="truncate flex items-center gap-1">
                          {offDay && (
                            <span className="bg-white rounded-full p-0.5" title="Dia em que a equipe normalmente não trabalha">
                              <OffDayIcon size={11} testId={`grid-offday-${dayKey}-${tk}`} />
                            </span>
                          )}
                          {name}
                        </span>
                        <span className="flex items-center gap-1 shrink-0">
                          <span className="bg-white/25 rounded-full px-1.5">
                            {arr.length}
                          </span>
                          <ChevronDown
                            size={12}
                            className={`transition-transform ${
                              isExpanded ? "" : "-rotate-90"
                            }`}
                          />
                        </span>
                      </button>
                      {isExpanded && (
                        <div className="p-1 space-y-0.5">
                          {arr.map((job) => (
                            <JobItem
                              key={job.id}
                              job={job}
                              done={job.status === "finalizada"}
                              offDay={offDay && job.status !== "cancelada"}
                              label={clientName(job.client_id)}
                              textColor={style.text}
                              onToggle={(e) => {
                                e.stopPropagation();
                                onToggleDone(job);
                              }}
                              onOpen={(ref) => onOpenQuick(job, ref)}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
        </div>
      </div>
    </div>
  );
}

function JobItem({ job, done, offDay, label, textColor, onToggle, onOpen }) {
  const ref = useRef(null);
  const cancelled = job.status === "cancelada";
  const time = job.scheduled_at
    ? new Date(job.scheduled_at).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";
  return (
    <div
      ref={ref}
      onClick={() => onOpen(ref)}
      data-testid={`monthly-job-${job.id}`}
      className="flex items-center gap-1.5 px-1 py-0.5 rounded cursor-pointer hover:bg-white/60 transition-colors"
    >
      <button
        onClick={onToggle}
        data-testid={`monthly-check-${job.id}`}
        className={`w-4 h-4 rounded flex items-center justify-center border shrink-0 transition-colors ${
          cancelled
            ? "bg-white border-red-500 text-red-600"
            : done
              ? "bg-emerald-500 border-emerald-500 text-white"
              : "bg-white border-slate-300 hover:border-emerald-400"
        }`}
        title={cancelled ? "Cancelada" : done ? "Concluído" : "Pendente"}
      >
        {cancelled ? <X size={12} strokeWidth={3.5} /> : done ? <Check size={11} strokeWidth={3} /> : null}
      </button>
      <span
        className={`text-[11px] flex items-center gap-1 min-w-0 ${
          cancelled ? "opacity-90" : done ? "line-through opacity-60" : ""
        }`}
        style={{
          color: cancelled ? "#dc2626" : textColor,
          ...(cancelled
            ? { textDecorationLine: "line-through", textDecorationStyle: "double", textDecorationColor: "#dc2626" }
            : {}),
        }}
      >
        {time && (
          <span className="font-bold tabular-nums shrink-0">{time}</span>
        )}
        <span className="truncate">{label}</span>
      </span>
      {offDay && <OffDayIcon size={12} testId={`grid-offday-job-${job.id}`} />}
    </div>
  );
}
